import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useOfflineOutbox } from '../hooks/useOfflineOutbox';
import { describeOutboxKind, OUTBOX_MAX_ATTEMPTS } from '../lib/offlineOutbox';
import { useToast } from './Toaster';
import { errorMessage } from '../lib/errors';
import { formatDateTime, pluralize } from '../lib/formatters';

/**
 * Sync indicator for the offline outbox. Rendered inside the shared bottom
 * status column (see StatusStack in AppLayout) so it sits above the mobile
 * nav and never overlaps toasts.
 *
 * States:
 *   - Online, nothing queued, nothing failed → hidden
 *   - Online, N pending → "Syncing N item(s)" + Retry
 *   - Offline → "Offline · N queued" (replays automatically on reconnect)
 *   - N failed (retry cap reached) → each entry listed with its last error,
 *     and Retry / Discard actions
 */
export function OfflineIndicator() {
  const { online, pending, failed, storageError, flush, discard, retry } = useOfflineOutbox();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // A replayed entry (automatic on reconnect, or a manual retry) writes rows
  // the pages have already cached. Whenever the pending count drops, refetch
  // the trip-scoped queries so "Reports filed" and today's report reflect
  // what actually landed in Postgres.
  const previousPending = useRef(pending.length);
  useEffect(() => {
    if (pending.length < previousPending.current) {
      void qc.invalidateQueries({ queryKey: ['daily-reports'] });
      void qc.invalidateQueries({ queryKey: ['site-intel'] });
      void qc.invalidateQueries({ queryKey: ['active-trips'] });
    }
    previousPending.current = pending.length;
  }, [pending.length, qc]);

  if (online && pending.length === 0 && failed.length === 0 && !storageError) return null;

  const onFlush = async () => {
    try {
      const results = await flush();
      const succeeded = results.filter((r) => r.ok).length;
      const failedNow = results.filter((r) => !r.ok && !r.skipped).length;
      if (results.length === 0) return;
      if (failedNow === 0) {
        notify(`Synced ${pluralize(succeeded, 'queued item')}.`, 'success');
      } else {
        notify(`${succeeded} synced, ${failedNow} still failing. Details in the sync panel.`, 'warning');
      }
    } catch (e) {
      notify(`Sync failed: ${errorMessage(e)}`, 'error');
    }
  };

  const onRetry = async (id: string) => {
    setBusyId(id);
    try {
      const result = await retry(id);
      if (!result) notify('That item is no longer in the queue.', 'info');
      else if (result.ok) notify('Queued item synced.', 'success');
      else notify(`Retry failed: ${result.error}`, 'error');
    } catch (e) {
      notify(`Retry failed: ${errorMessage(e)}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const onDiscard = async (id: string, label: string) => {
    setBusyId(id);
    try {
      await discard(id);
      notify(`${label} discarded. It was not saved to the portal.`, 'info');
    } catch (e) {
      notify(`Could not discard: ${errorMessage(e)}`, 'error');
    } finally {
      setBusyId(null);
    }
  };

  const summary = !online
    ? `Offline · ${pluralize(pending.length, 'change')} queued`
    : pending.length > 0
      ? `Syncing ${pluralize(pending.length, 'change')}`
      : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto w-full max-w-md rounded-lg border border-border bg-surface-strong text-sm text-text shadow-lg"
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <span
          aria-hidden="true"
          className={`inline-block h-2 w-2 shrink-0 rounded-full ${
            !online ? 'bg-warning' : failed.length > 0 && pending.length === 0 ? 'bg-error' : 'bg-success animate-pulse'
          }`}
        />
        <p className="min-w-0 flex-1 truncate">
          {summary ?? (failed.length > 0 ? `${pluralize(failed.length, 'change')} could not be synced` : 'Sync')}
          {summary && failed.length > 0 ? ` · ${failed.length} failed` : ''}
        </p>
        {online && pending.length > 0 && (
          <button type="button" onClick={() => void onFlush()} className="btn-ghost min-h-[40px] px-3 text-xs">
            Retry now
          </button>
        )}
        {failed.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="btn-ghost min-h-[40px] px-3 text-xs"
          >
            {expanded ? 'Hide' : 'Details'}
          </button>
        )}
      </div>

      {!online && (
        <p className="border-t border-border px-3 py-2 text-xs text-text-muted">
          Changes are stored on this device and sent automatically when the connection returns.
        </p>
      )}

      {storageError && (
        <p className="border-t border-border px-3 py-2 text-xs text-error">
          Offline storage is unavailable ({storageError}). Changes made while offline will not be kept.
        </p>
      )}

      {expanded && failed.length > 0 && (
        <ul className="divide-y divide-border border-t border-border">
          {failed.map((entry) => {
            const label = describeOutboxKind(entry.kind);
            const busy = busyId === entry.id;
            return (
              <li key={entry.id} className="px-3 py-2">
                <p className="text-xs font-medium text-text">
                  {label}
                  <span className="font-normal text-text-muted"> · queued {formatDateTime(entry.queuedAt)}</span>
                </p>
                <p className="mt-0.5 break-words text-xs text-error">
                  Failed {OUTBOX_MAX_ATTEMPTS} times. {entry.lastError ?? 'Unknown error'}
                </p>
                <div className="mt-1 flex gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onRetry(entry.id)}
                    className="btn-secondary min-h-[40px] px-3 text-xs"
                  >
                    Retry
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => void onDiscard(entry.id, label)}
                    className="btn-danger min-h-[40px] px-3 text-xs"
                  >
                    Discard
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
